'use client';

import React from 'react';
import { ImageModal } from '../ProductSelectionSection/ImageModal';
import { HeroDiscountOffer } from '../secondaryOffers';
import { OffersDataType } from '../OffersCarousel';
import { HsaFsaFlexTitle } from 'components/shared/HsaFsaFlexTitle';
import { SecondaryOfferDataType } from '../SecondaryOfferComponent';
import { isMCFCountry } from 'lib/region/countryUtils';
import {
  getInstallmentAmount,
  getInstallmentConfig,
} from 'utils/installmentConfig';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  isHeroCategory,
  isHeroVerificationRegion,
  resolveHeroPriceCategory,
} from 'lib/commerce/heroes/heroVerification';

/**
 * Wraps the word "Klarna" within a translated string in an external link to
 * klarna.com. Falls back to the plain string when "Klarna" is not present, so
 * other regions/locales are unaffected. Handles multiple occurrences.
 */
function linkifyKlarna(text: string): React.ReactNode {
  const parts = text.split('Klarna');
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {i > 0 && (
        <a
          href="https://www.klarna.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          Klarna
        </a>
      )}
      {part}
    </React.Fragment>
  ));
}

type Translate = (
  key: string,
  values?: Record<string, string | number>,
) => string;

export type OffersRouter = {
  push: (url: string) => void;
  query?: Record<string, string | string[] | undefined>;
};

export type GetOffersForRegionParams = {
  t: Translate;
  regionSlug: string;
  router: OffersRouter;
  product?: 'ring-air' | 'ring-pro' | 'photon';
  /** Opens the heroes modal in place instead of the info sheet + /heroes link. */
  onHeroVerify?: () => void;
};

/**
 * Returns offersData and secondaryOfferData for the given region.
 * Shared by Ring AIR and Ring PRO buy pages for region/country-based offers.
 */
export function getOffersForRegion(
  region: string,
  params: GetOffersForRegionParams,
): {
  offersData: OffersDataType[];
  secondaryOfferData: SecondaryOfferDataType[];
} {
  const { t, regionSlug, router, product = 'ring-air', onHeroVerify } = params;
  const r = region.toUpperCase();

  const heroSecondaryOffer: SecondaryOfferDataType = {
    // US verifies with ID.me / GovX, so the chip names them.
    title:
      onHeroVerify && isHeroVerificationRegion(r)
        ? t('offersByRegion.content.specialPricingHeroesVerify')
        : t('offersByRegion.content.specialPricingHeroes'),
    onClick: onHeroVerify,
    modal: (
      <ImageModal
        ctaText={t('offersByRegion.imageModal.ctaText.learnMore')}
        ctaOnClick={() => {
          const query = new URLSearchParams();
          Object.entries(router.query ?? {}).forEach(([key, value]) => {
            if (Array.isArray(value))
              value.forEach((v) => query.append(key, v));
            else if (value !== undefined) query.append(key, value);
          });
          if (product === 'ring-pro') query.set('product', 'ring-pro');
          const qs = query.toString();
          router.push(`/${regionSlug}/heroes${qs ? `?${qs}` : ''}`);
        }}
      >
        <HeroDiscountOffer
          product={product === 'ring-pro' ? 'ring-pro' : 'ring-air'}
        />
      </ImageModal>
    ),
  };

  // Hide the heroes pill once a valid hero category is selected
  // (`heroCategory` or an ID.me code), on every product.
  const queryParams = {
    get: (key: string) => {
      const v = router.query?.[key];
      return (Array.isArray(v) ? v[0] : v) ?? null;
    },
  };
  // Hide the chip only once a hero price applies; an unverified US veterans
  // link gets no price, so it keeps the chip to verify.
  const heroOffers = isHeroCategory(resolveHeroPriceCategory(queryParams, r))
    ? []
    : [heroSecondaryOffer];

  // US / PR
  if (r === 'US' || r === 'PR') {
    return {
      offersData: [
        {
          title: <HsaFsaFlexTitle />,
          image: ['web_v2/icons/primary_offers/hsafsa.png'],
          // Shown in the row under the price; "See all" opens every offer.
          displayValue: {
            icons: ['web_v2/icons/primary_offers/hsafsa.png'],
            text: <HsaFsaFlexTitle />,
          },
        },
        {
          title: (
            <>{linkifyKlarna(t('offersByRegion.content.splitPaymentsUsing'))}</>
          ),
          image: [
            'web_v2/icons/primary_offers/klara.png',
            'web_v2/icons/primary_offers/affirmm.png',
            'web_v2/icons/primary_offers/afterpay.png',
          ],
          displayValue: {
            icons: [
              'web_v2/icons/primary_offers/klara.png',
              'web_v2/icons/primary_offers/affirmm.png',
            ],
            text: (
              <>
                {linkifyKlarna(t('offersByRegion.content.splitPaymentsUsing2'))}{' '}
              </>
            ),
          },
        },
        {
          title: <>{t('offersByRegion.content.shopPayPay')}</>,
          image: ['web_v2/icons/primary_offers/shoppay.png'],
          imageType: 'square',
        },
        {
          title: <>{t('offersByRegion.content.instantPaymentPaypal')}</>,
          image: [
            'web_v2/icons/primary_offers/paypal.png',
            'web_v2/icons/primary_offers/applepay.png',
            'web_v2/icons/primary_offers/gpay.png',
          ],
        },
        {
          title: <>{t('offersByRegion.content.cryptoPaymentsUsdc')}</>,
          image: ['web_v2/icons/primary_offers/usdc-2.png'],
        },
      ],
      secondaryOfferData: heroOffers,
    };
  }

  // Canada
  if (r === 'CA') {
    return {
      offersData: [
        {
          title: <>{t('offersByRegion.content.splitPaymentsUsing')}</>,
          image: [
            'web_v2/icons/primary_offers/klara.png',
            'web_v2/icons/primary_offers/affirmm.png',
            'web_v2/icons/primary_offers/afterpay.png',
          ],
          displayValue: {
            icons: [
              'web_v2/icons/primary_offers/klara.png',
              'web_v2/icons/primary_offers/affirmm.png',
            ],
            text: <>{t('offersByRegion.content.splitPaymentsUsing3')}</>,
          },
        },
        {
          title: <>{t('offersByRegion.content.shopPayPay')}</>,
          image: ['web_v2/icons/primary_offers/shoppay.png'],
          imageType: 'square',
        },
        {
          title: <>{t('offersByRegion.content.instantPaymentPaypal')}</>,
          image: [
            'web_v2/icons/primary_offers/paypal.png',
            'web_v2/icons/primary_offers/applepay.png',
            'web_v2/icons/primary_offers/gpay.png',
          ],
        },
      ],
      secondaryOfferData: heroOffers,
    };
  }

  // India
  if (r === 'IN') {
    const inOffers: OffersDataType[] = [
      {
        title: (
          <>
            {t('ring.productSelection.in.offerD.a')}{' '}
            {t('ring.productSelection.in.offerD.b')}{' '}
            <span style={{ whiteSpace: 'nowrap' }}>₹ 1,187/month</span>
          </>
        ),
        image: ['web_v2/icons/primary_offers/0-percent.png'],
        displayValue: {
          icons: ['web_v2/icons/primary_offers/0-percent-v2.png'],
          text: <>24 months EMI option</>,
        },
      },
      {
        title: <>CRED pay | Save and earn rewards</>,
        image: ['web_v2/icons/primary_offers/cred.png'],
      },
      {
        title: <>Easy payments with UPI</>,
        image: ['web_v2/icons/primary_offers/upi.png'],
      },
    ];
    return {
      offersData: inOffers,
      secondaryOfferData: heroOffers,
    };
  }

  // AE / GCC (QA, BH, KW, OM)
  if (['AE', 'QA', 'BH', 'KW', 'OM'].includes(r)) {
    return {
      offersData: [
        {
          title: <>{t('offersByRegion.content.instantPaymentApple')}</>,
          image: ['web_v2/icons/primary_offers/applepay.png'],
        },
        {
          title: <>{t('offersByRegion.content.splitPaymentsTabby')}</>,
          image: [
            'web_v2/icons/primary_offers/icon.png',
            'web_v2/icons/primary_offers/tamara_new.png',
            'web_v2/icons/primary_offers/cashew.png',
          ],
          displayValue: {
            icons: [
              'web_v2/icons/primary_offers/icon.png',
              'web_v2/icons/primary_offers/tamara_new.png',
            ],
            text: <>{t('offersByRegion.content.splitPaymentsUsing4')}</>,
          },
        },
        {
          title: <>{t('ring.productSelection.in.offerC.a')}</>,
          image: ['web_v2/icons/primary_offers/cod.png'],
        },
      ],
      // AE drops the Heroes Of The World card; the other GCC regions keep it.
      secondaryOfferData: r === 'AE' ? [] : heroOffers,
    };
  }

  // MCF (EU/GB etc.)
  if (isMCFCountry(r)) {
    return {
      offersData: [
        {
          title: <>{t('offersByRegion.content.oneTapCheckout')}</>,
          image: ['web_v2/icons/primary_offers/shoppay.png'],
        },
        {
          title: <>{t('offersByRegion.content.instantPaymentPaypal2')}</>,
          image: ['web_v2/icons/primary_offers/paypal.png'],
          imageType: 'square',
        },
        {
          title: <>{t('offersByRegion.content.lowAprFinancing2')}</>,
          image: ['web_v2/icons/primary_offers/klara.png'],
          imageType: 'square',
          displayValue: {
            icons: ['web_v2/icons/primary_offers/klara.png'],
            text: <>{t('offersByRegion.content.splitPaymentsUsing5')}</>,
          },
        },
      ],
      secondaryOfferData: heroOffers,
    };
  }

  // Default (Non-MCF: AU, CA, JP, SA, MX, ZA, ROW, etc.)
  return {
    offersData: [
      {
        title: <>{t('offersByRegion.content.expressCheckoutPaypal')}</>,
        image: ['web_v2/icons/primary_offers/paypal.png'],
      },
      {
        title: <>{t('offersByRegion.content.instantPaymentApple')}</>,
        image: ['web_v2/icons/primary_offers/applepay.png'],
      },
    ],
    secondaryOfferData: heroOffers,
  };
}

/**
 * India no-cost-EMI monthly label. `Math.ceil` is the point: a raw division
 * renders ₹1,187.46 where production shows ₹ 1,188 (main 35735ce2a).
 */
export function getIndiaEmiMonthLabel(emiBasePrice: number): string | null {
  const months = getInstallmentConfig(ShopifyStore.IN)?.months ?? 24;
  if (!(emiBasePrice > 0)) return null;
  return `₹ ${formatPriceWithComma(
    getInstallmentAmount(emiBasePrice, months),
    Currency.inr,
  )}/month`;
}

/**
 * B1 "starting at" ring price under the active discount.
 * When the shopper is on the B1 finish (selectedPrice ≈ b1), matches the price
 * line. When on a pricier finish with a %-style discount, re-applies the same
 * rate to B1.
 */
export function getB1DiscountedEmiBasePrice(
  b1BasePrice: number,
  selectedPrice: number,
  discountAmount: number,
): number {
  if (!(b1BasePrice > 0)) return 0;
  if (!(discountAmount > 0) || !(selectedPrice > 0)) return b1BasePrice;

  if (Math.abs(selectedPrice - b1BasePrice) < 0.01) {
    return Math.max(0, b1BasePrice - discountAmount);
  }

  const rate = Math.min(1, discountAmount / selectedPrice);
  return Math.max(0, b1BasePrice * (1 - rate));
}

/**
 * Rewrite the IN no-cost-EMI offer row to use a live / discounted base price.
 * Without this the row renders a HARDCODED catalog string
 * (`ring.productSelection.in.offerD.187Month`) that silently drifts from the
 * real price every time it changes.
 */
export function withIndiaEmiOfferPrice(
  offers: OffersDataType[] | undefined,
  t: Translate,
  emiBasePrice: number,
): OffersDataType[] | undefined {
  if (!offers?.length || !(emiBasePrice > 0)) return offers;
  const label = getIndiaEmiMonthLabel(emiBasePrice);
  if (!label) return offers;

  return offers.map((offer) => {
    const isEmiOffer = offer.image?.[0]?.includes('0-percent');
    if (!isEmiOffer) return offer;
    return {
      ...offer,
      title: (
        <>
          {t('ring.productSelection.in.offerD.a')}{' '}
          {t('ring.productSelection.in.offerD.b')}{' '}
          <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
        </>
      ),
    };
  });
}
